import { updateComplianceItem, updateTask } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  ComplianceStatus,
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useState } from 'react';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { viewUploadedFile } from '../../utils';
import { ServiceRequestTaskLabel } from '../labels';
import { ServiceRequestTaskIconConfig } from '../taskIconConfig';
import { ComplianceItemFileUploadModal } from './UploadAcknowledgement';

export const AgentAcknowledgementTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const [file, setFile] = useState<FileBasic>();

  const uploadAcknowledgementModalVisibility = useToggle();

  const openFile = async () => {
    const fileId = serviceRequest?.complianceItem?.acknowledgements?.length
      ? Math.max(
          ...serviceRequest.complianceItem.acknowledgements.map((ack) => ack.id)
        )
      : null;
    if (fileId) await viewUploadedFile(fileId);
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads acknowledgement" />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads acknowledgement" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openFile}>
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Acknowledgement uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload acknowledgement" />,
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadAcknowledgementModalVisibility.show}
              >
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload acknowledgement" />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openFile}>
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Acknowledgement uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ComplianceItemFileUploadModal
        serviceRequest={serviceRequest}
        mutate={mutate}
        companyId={serviceRequest.companyId}
        isOpen={uploadAcknowledgementModalVisibility.visible}
        setIsOpen={uploadAcknowledgementModalVisibility.toggle}
        file={file}
        setFile={setFile}
        title="Upload acknowledgement"
        documentType={LegacyDocumentType.ComplianceAcknowledgement}
        onUploadSuccess={async () => {
          const updateServiceRequestRes = await updateTask({
            id: task.id,
            data: { status: TaskStatus.COMPLETED },
          });
          if (updateServiceRequestRes?.ok && serviceRequest.complianceItemId) {
            await updateComplianceItem(
              serviceRequest.complianceItemId,
              { status: ComplianceStatus.COMPLETED },
              undefined
            );
          }
          await mutate();
        }}
        accept={{
          'application/pdf': ['.pdf', '.xlsx', '.docx', '.csv'],
          'image/*': ['.png', '.jpg', '.jpeg'],
        }}
      />
    </div>
  );
};
